/**
 * OfferIntelligence — /v3/intel/offer-intel
 *
 * BP-028 workspace tab: multi-strategy offer generation and comparison.
 *
 * Layout:
 *   • Property picker (from workspace properties)
 *   • Recommended Strategy hero card (with Claude coach note)
 *   • Offer Comparison Matrix — 5 strategy cards side-by-side
 *   • Offer Timeline widget (close-days per strategy)
 *   • Negotiation Console (per-strategy counter-offer tracker)
 *
 * All numbers come from POST /generate. Every card exposes its
 * `reasons` and `warnings` so the math is auditable.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Coins, KeyRound, Handshake, RefreshCcw, Layers, Sparkles,
  DollarSign, Timer, Percent, AlertTriangle, Loader2, TrendingUp,
  Award, ArrowRight, MessageSquare, ShieldCheck,
} from "lucide-react";

const STRATEGY_META = {
  cash:            { label: "Cash",            icon: Coins,     accent: "#F59E0B" },
  subject_to:      { label: "Subject-To",      icon: KeyRound,  accent: "#8B5CF6" },
  seller_finance:  { label: "Seller Finance",  icon: Handshake, accent: "#0EA5E9" },
  novation:        { label: "Novation",        icon: RefreshCcw,accent: "#10B981" },
  hybrid:          { label: "Hybrid",          icon: Layers,    accent: "#EA580C" },
};

const RISK_META = {
  low:      { label: "Low risk",       color: "text-emerald-600", bg: "bg-emerald-500/10", border: "border-emerald-500/30" },
  moderate: { label: "Moderate risk",  color: "text-amber-600",   bg: "bg-amber-500/10",   border: "border-amber-500/30" },
  elevated: { label: "Elevated risk",  color: "text-orange-600",  bg: "bg-orange-500/10",  border: "border-orange-500/30" },
  high:     { label: "High risk",      color: "text-red-600",     bg: "bg-red-500/10",     border: "border-red-500/30" },
};

const fmtMoney = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD",
                                    maximumFractionDigits: 0 }).format(n || 0);
const fmtPct   = (n) => `${Math.round((n || 0) * 100)}%`;

// ─────────────────────────────── page ────────────────────────────────────

export default function OfferIntelligence() {
  const [properties, setProperties] = useState([]);
  const [propId, setPropId] = useState("");
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Load ranked leads to populate the picker (falls back to raw properties list).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/v3/acquisition-intel/top-leads?limit=50");
        if (!cancelled && Array.isArray(data.items)) {
          setProperties(data.items.map((r) => ({
            id: r.property_id, label: r.property_address, heat: r.heat,
          })));
          if (data.items[0] && !propId) setPropId(data.items[0].property_id);
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Auto-load matrix whenever a property is picked.
  useEffect(() => {
    if (!propId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/v3/offer-intel/${propId}`);
        if (!cancelled) setMatrix(data);
      } catch (err) {
        if (err?.response?.status === 404) {
          if (!cancelled) setMatrix(null);
        } else {
          toast.error("Failed to load offer intelligence.");
        }
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [propId]);

  const generate = useCallback(async () => {
    if (!propId) return;
    setGenerating(true);
    try {
      const { data } = await api.post("/v3/offer-intel/generate", {
        property_id: propId, force: true,
      });
      setMatrix(data);
      toast.success(`Generated ${data.strategies?.length ?? 0} offer strategies.`);
    } catch { toast.error("Offer generation failed."); }
    finally { setGenerating(false); }
  }, [propId]);

  const recommended = matrix?.strategies?.find((s) => s.strategy === matrix.recommended);
  const sortedByProfit = useMemo(
    () => [...(matrix?.strategies || [])].sort(
      (a, b) => (b.math?.expected_profit || 0) - (a.math?.expected_profit || 0)
    ),
    [matrix]
  );

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="offer-intel-page">
      {/* Header + property picker */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-emerald-600">
            <Sparkles className="w-3.5 h-3.5" />
            BP-028 · Deal Strategy
          </div>
          <h1 className="mt-1 text-3xl font-bold text-slate-900 tracking-tight">Offer Intelligence</h1>
          <p className="text-sm text-slate-500 mt-1">
            Five offer strategies. One recommended. Every dollar audited.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            data-testid="offer-intel-property-picker"
            value={propId}
            onChange={(e) => setPropId(e.target.value)}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm min-w-[280px] focus:outline-none focus:ring-2 focus:ring-amber-400"
          >
            {properties.length === 0 && <option value="">No properties yet</option>}
            {properties.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label} {p.heat ? `· ${p.heat.toUpperCase()}` : ""}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={generate}
            disabled={!propId || generating}
            data-testid="offer-intel-generate"
            className="inline-flex items-center gap-1.5 rounded-lg text-white text-xs font-bold px-3 py-2 shadow disabled:opacity-60"
            style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #EA580C)" }}
          >
            {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            Generate Offers
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      )}

      {!loading && !matrix && (
        <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white p-10 text-center"
             data-testid="offer-intel-empty">
          <Sparkles className="w-8 h-8 text-amber-500 mx-auto" />
          <h3 className="mt-3 text-lg font-bold text-slate-900">No offer intelligence yet</h3>
          <p className="mt-1 text-sm text-slate-500">
            Pick a property above and click <b>Generate Offers</b> to build the comparison matrix.
          </p>
        </div>
      )}

      {matrix && (
        <>
          {/* Property snapshot */}
          <PropertySnapshot matrix={matrix} />

          {/* Recommended Strategy hero */}
          {recommended && (
            <RecommendedCard recommended={recommended} matrix={matrix} />
          )}

          {/* Offer Comparison Matrix */}
          <OfferComparisonMatrix strategies={matrix.strategies} recommended={matrix.recommended} />

          {/* Offer Timeline widget */}
          <OfferTimelineCard strategies={sortedByProfit} recommended={matrix.recommended} />

          {/* Negotiation console */}
          <NegotiationConsole propertyId={matrix.property_id} strategies={matrix.strategies} />
        </>
      )}
    </div>
  );
}

/* ─────────────────────────── property snapshot ────────────────────────── */

function PropertySnapshot({ matrix }) {
  const items = [
    { label: "ARV",           value: fmtMoney(matrix.arv) },
    { label: "Rehab",         value: fmtMoney(matrix.rehab) },
    { label: "Loan balance",  value: fmtMoney(matrix.loan_balance) },
    { label: "Equity",        value: fmtMoney(matrix.equity_estimate) },
  ];
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="offer-intel-snapshot">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-black tracking-widest uppercase text-emerald-600">Property snapshot</div>
          <h2 className="text-lg font-bold text-slate-900">{matrix.property_address}</h2>
        </div>
        <span className="text-[11px] text-slate-400 uppercase tracking-widest">
          Generated {new Date(matrix.generated_at).toLocaleString()}
        </span>
      </div>
      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        {items.map((it) => (
          <div key={it.label} className="rounded-xl bg-slate-50 border border-slate-100 px-4 py-3">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{it.label}</div>
            <div className="mt-1 text-lg font-bold text-slate-900 tabular-nums">{it.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────── recommended card ─────────────────────────── */

function RecommendedCard({ recommended, matrix }) {
  const meta = STRATEGY_META[recommended.strategy];
  const Icon = meta.icon;
  return (
    <div className="rounded-2xl border-2 border-amber-300/60 bg-gradient-to-br from-amber-50 via-white to-white p-6 shadow-md"
         data-testid="offer-intel-recommended">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center gap-1.5 rounded-full text-[10px] font-black tracking-widest uppercase px-2.5 py-1 text-white"
              style={{ backgroundImage: "linear-gradient(90deg, #F59E0B, #EA580C)" }}>
          <Award className="w-3 h-3" /> AI Recommended
        </span>
        <span className="text-[10px] font-black tracking-widest uppercase text-slate-400">
          {matrix.ai_source === "claude" ? "Claude" : "Deterministic"}
        </span>
      </div>
      <div className="mt-3 flex items-start gap-4 flex-wrap">
        <div className="grid place-items-center h-14 w-14 rounded-xl shadow"
             style={{ background: `linear-gradient(135deg, ${meta.accent}, #EA580C)` }}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1 min-w-[260px]">
          <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500">Recommended strategy</div>
          <h2 className="text-2xl font-bold text-slate-900">{recommended.label}</h2>
          <p className="mt-2 text-sm text-slate-700 leading-relaxed">
            {matrix.recommendation_reason}
          </p>
        </div>
      </div>
      {matrix.ai_summary && (
        <div className="mt-4 rounded-xl bg-white border border-amber-200 p-4 text-sm leading-relaxed text-slate-700 whitespace-pre-line"
             data-testid="offer-intel-ai-summary">
          {matrix.ai_summary}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────── comparison matrix ────────────────────────── */

function OfferComparisonMatrix({ strategies, recommended }) {
  return (
    <div className="space-y-3" data-testid="offer-intel-matrix">
      <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-emerald-600">
        <TrendingUp className="w-3.5 h-3.5" /> Offer Comparison Matrix
      </div>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
        {strategies.map((s) => (
          <StrategyCard key={s.strategy} strategy={s} isRecommended={s.strategy === recommended} />
        ))}
      </div>
    </div>
  );
}

function StrategyCard({ strategy, isRecommended }) {
  const meta = STRATEGY_META[strategy.strategy] || {};
  const risk = RISK_META[strategy.risk_band] || RISK_META.moderate;
  const Icon = meta.icon || Coins;
  const m = strategy.math || {};

  return (
    <div className={`rounded-2xl border ${isRecommended ? "border-amber-300 shadow-md" : "border-slate-200"} bg-white p-5`}
         data-testid={`offer-intel-strategy-${strategy.strategy}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="grid place-items-center h-9 w-9 rounded-lg text-white"
              style={{ background: `linear-gradient(135deg, ${meta.accent || "#F59E0B"}, #EA580C)` }}>
          <Icon className="w-4 h-4" />
        </span>
        <div className="flex-1">
          <div className="text-[10px] font-black tracking-widest uppercase text-slate-500">Strategy</div>
          <div className="text-base font-bold text-slate-900 leading-tight">{strategy.label}</div>
        </div>
        {isRecommended && (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 border border-amber-400/40 text-amber-700 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest">
            <Award className="w-3 h-3" /> Rec
          </span>
        )}
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 text-[12px]">
        <MetricPill icon={DollarSign} label="Offer"     value={fmtMoney(m.offer_amount)} />
        <MetricPill icon={DollarSign} label="Net to seller" value={fmtMoney(m.net_to_seller)} />
        <MetricPill icon={TrendingUp} label="Est. profit" value={fmtMoney(m.expected_profit)} highlight />
        <MetricPill icon={Percent}    label="Est. ROI"    value={fmtPct(m.expected_roi)} />
        {m.term_months > 0 && (
          <MetricPill icon={DollarSign} label={`P&I / mo`} value={fmtMoney(m.monthly_payment)} />
        )}
        <MetricPill icon={Timer}      label="Close"       value={`${m.close_days}d`} />
      </div>

      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${risk.bg} ${risk.color} border ${risk.border}`}>
          <ShieldCheck className="w-3 h-3" /> {risk.label}
        </span>
        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
          Fit {strategy.fit_score}
        </span>
      </div>

      {strategy.reasons?.length > 0 && (
        <ul className="mt-3 space-y-1 pl-4 list-disc text-[11.5px] text-slate-600 leading-relaxed">
          {strategy.reasons.slice(0, 3).map((r, i) => (
            <li key={i}><span className="font-semibold text-slate-800">{r.label}</span>{r.detail ? ` — ${r.detail}` : ""}</li>
          ))}
        </ul>
      )}

      {strategy.warnings?.length > 0 && (
        <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 flex items-start gap-2">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-[11.5px] text-amber-800 leading-relaxed space-y-1">
            {strategy.warnings.map((w, i) => <div key={i}>{w}</div>)}
          </div>
        </div>
      )}
    </div>
  );
}

function MetricPill({ icon: Icon, label, value, highlight }) {
  return (
    <div className={`rounded-lg px-2.5 py-2 border ${highlight ? "border-emerald-200 bg-emerald-50/50" : "border-slate-100 bg-slate-50"}`}>
      <div className="flex items-center gap-1 text-[9.5px] font-bold uppercase tracking-widest text-slate-500">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className={`mt-0.5 font-bold tabular-nums ${highlight ? "text-emerald-700" : "text-slate-900"}`}>
        {value}
      </div>
    </div>
  );
}

/* ─────────────────────────── offer timeline ─────────────────────────── */

function OfferTimelineCard({ strategies, recommended }) {
  const maxDays = Math.max(...strategies.map((s) => s.math?.close_days || 0), 1);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="offer-intel-timeline">
      <div className="inline-flex items-center gap-2 text-[10px] font-black tracking-widest uppercase text-emerald-600">
        <Timer className="w-3.5 h-3.5" /> Offer Timeline · Close velocity
      </div>
      <div className="mt-4 space-y-2.5">
        {strategies.map((s) => {
          const days = s.math?.close_days || 0;
          const pct = maxDays ? (days / maxDays) * 100 : 0;
          const meta = STRATEGY_META[s.strategy];
          const Icon = meta.icon;
          const isRec = s.strategy === recommended;
          return (
            <div key={s.strategy} className="flex items-center gap-3">
              <div className="w-36 flex items-center gap-2">
                <Icon className="w-3.5 h-3.5" style={{ color: meta.accent }} />
                <span className="text-[12px] font-semibold text-slate-700">{s.label}</span>
              </div>
              <div className="flex-1 h-2.5 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full rounded-full"
                     style={{ width: `${pct}%`,
                              background: isRec ? "linear-gradient(90deg, #F59E0B, #EA580C)" : "#94A3B8" }} />
              </div>
              <div className="w-16 text-right text-[12px] font-bold text-slate-700 tabular-nums">
                {days}d
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─────────────────────────── negotiation console ──────────────────────── */

function NegotiationConsole({ propertyId, strategies }) {
  const [strategy, setStrategy] = useState(strategies[0]?.strategy || "cash");
  const [ourOffer, setOurOffer] = useState("");
  const [sellerCounter, setSellerCounter] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = useCallback(async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/v3/offer-intel/negotiate", {
        property_id: propertyId, strategy,
        our_offer: Number(ourOffer) || null,
        seller_counter: Number(sellerCounter) || null,
      });
      setResult(data);
      toast.success("Negotiation logged.");
    } catch { toast.error("Negotiation update failed."); }
    finally { setBusy(false); }
  }, [propertyId, strategy, ourOffer, sellerCounter]);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="offer-intel-negotiation">
      <div className="inline-flex items-center gap-2 text-[10px] font-black tracking-widest uppercase text-emerald-600">
        <MessageSquare className="w-3.5 h-3.5" /> Negotiation Console
      </div>
      <form onSubmit={submit} className="mt-3 grid md:grid-cols-4 gap-2">
        <select value={strategy} onChange={(e) => setStrategy(e.target.value)}
                data-testid="offer-intel-neg-strategy"
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
          {strategies.map((s) => (
            <option key={s.strategy} value={s.strategy}>{s.label}</option>
          ))}
        </select>
        <input type="number" placeholder="Our offer" value={ourOffer}
               onChange={(e) => setOurOffer(e.target.value)}
               data-testid="offer-intel-neg-our"
               className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" />
        <input type="number" placeholder="Seller counter" value={sellerCounter}
               onChange={(e) => setSellerCounter(e.target.value)}
               data-testid="offer-intel-neg-seller"
               className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" />
        <button type="submit" disabled={busy}
                data-testid="offer-intel-neg-submit"
                className="inline-flex items-center justify-center gap-1.5 rounded-lg text-white text-xs font-bold px-3 py-2 shadow disabled:opacity-60"
                style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #EA580C)" }}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
          Log & Get Counter
        </button>
      </form>

      {result && (
        <div className="mt-3 rounded-lg bg-emerald-50 border border-emerald-200 p-4">
          <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">
            Recommended counter
          </div>
          <div className="mt-1 text-2xl font-bold text-slate-900 tabular-nums">
            {fmtMoney(result.recommended_counter)}
          </div>
          <p className="mt-1 text-[13px] text-slate-700">{result.ai_coach_note}</p>
          <div className="mt-2 text-[11px] text-slate-500">
            Walk-away floor: <span className="font-bold tabular-nums">{fmtMoney(result.walk_away_above)}</span>
            {" · "}State: <span className="font-bold uppercase tracking-widest">{result.state}</span>
          </div>
        </div>
      )}
    </div>
  );
}
